import { FC, useMemo } from "react";
import styled from "@emotion/styled";
import { syntaxTreeModule } from "@mem-labs/common-editor";

type Props = {
  clippedTextContent?: string | null;
  clippedRichTextContent?: string | null;
  isLastInGroup?: boolean;
};

export const SessionClippedTextContent: FC<Props> = ({
  clippedTextContent,
  clippedRichTextContent,
  isLastInGroup,
}) => {
  const renderableTextContent: string | null = useMemo(() => {
    if (clippedRichTextContent) {
      try {
        const htmlContent = syntaxTreeModule.convertHtmlToMdx({
          htmlContent: clippedRichTextContent,
        });

        const plaintext = syntaxTreeModule.convertMdxToPlaintext({
          mdxContent: htmlContent,
        });

        return plaintext;
      } catch (_unknownErr) {
        // Fall back to text content
      }
    }

    return clippedTextContent ?? null;
  }, [clippedRichTextContent, clippedTextContent]);

  if (!renderableTextContent) {
    return null;
  }

  return (
    <ClippedTextContentWrapper isLastInGroup={isLastInGroup}>
      {renderableTextContent}
    </ClippedTextContentWrapper>
  );
};

const ClippedTextContentWrapper = styled.div<{ isLastInGroup?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.xsmall};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: ${({ theme }) => theme.lineHeights.xsmall};
  color: ${({ theme }) => theme.colors.grey.x600};
  padding: ${({ theme }) => theme.spacing.md};
  width: 100%;
  max-height: 220px;
  overflow: hidden;
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.grey.x200};
  border-radius: ${({ isLastInGroup }) => (isLastInGroup ? `24px 24px 0 24px` : `24px`)};
  white-space: pre-wrap;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      ${({ theme }) => theme.colors.grey.x100}99 50%,
      ${({ theme }) => theme.colors.grey.x100} 100%
    );
    pointer-events: none;
  }
`;
